/**
 * The types, constants and box geometry that planning a PDF citation's highlight
 * (`./pdfCitationPlan`) and painting it (`./pdfCitationView`) share. They live here so
 * that the dependency runs one way: both modules import from this one, and planning no
 * longer reaches into the painting module.
 *
 * Every rectangle is in REFERENCE_SCALE units, the units `textLayer` is baked in and
 * the overlay is sized in.
 */

/**
 * Whitespace runs, hoisted to a constant rather than written inline. A regex
 * literal inside a call loses the token after it in the complexity analyser
 * (see docs/planning/techdebt-complex-functions.md), which unbalances every
 * function that follows. `split` never reads or writes `lastIndex`, so sharing
 * one instance is safe.
 */
export const WHITESPACE_RUN = /\s+/;

/**
 * A text item's `y` is its baseline, so a box covering the glyphs starts this
 * fraction of the item's height above it.
 */
export const HIGHLIGHT_ASCENT_RATIO = 0.85;

/** One highlight rectangle, in REFERENCE_SCALE units within its page's overlay. */
export interface HighlightRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Highlight rectangles keyed by page number. */
export type PageHighlightPlan = Record<number, HighlightRect[]>;

/** The parts of an extracted text item that planning and the fallback highlight read. */
export interface CitationTextItem {
  text: string;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The box covering a text item's glyphs, raised from its baseline by the ascent. */
export const itemToRect = (item: CitationTextItem): HighlightRect => ({
  left: item.x,
  top: item.y - item.height * HIGHLIGHT_ASCENT_RATIO,
  width: item.width,
  height: item.height,
});
